import {
  BuildOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  IdcardOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Tag, Tooltip, Typography } from 'antd';
import { coloresEstados } from 'assets/data/estadosPresupuesto';
import dayjs from 'libs/dayjs';

const { Text } = Typography;

/**
 * HOC que inyecta columns + dataSource derivados del `type` al componente
 * Table. Acepta `data` con shape agregada (modo legacy) o `rows` directos
 * (modo paginado — preferido). Ver useDocumentosList.
 */
// eslint-disable-next-line react/display-name
const whitDoc = (Component) => (props) => {
  const { type, data, rows, customActions, ...tableProps } = props;
  const columns = getColumns(type, customActions);
  const dataSource = rows ?? getData(data, type);

  return (
    <Component
      columns={columns}
      dataSource={dataSource}
      rowKey="id"
      size="middle"
      scroll={{ x: 1000 }}
      {...tableProps}
    />
  );
};

function getData(data, type) {
  switch (type) {
    case 'preventivos':
      return [];
    case 'mantencionesDep':
      return data?.mantencionesDep || [];
    case 'mantenciones':
      return data?.mantenciones || [];
    case 'odt':
      return [];
    case 'presupuestos':
      return data?.presupuestos || [];
    case 'comprobantes':
      return data?.comprobantes || [];
    default:
      return [];
  }
}

const dateCol = (dataIndex, label, icon) => ({
  title: <TitleWithIcon icon={icon}>{label}</TitleWithIcon>,
  dataIndex,
  width: 120,
  render: (value) => (
    <Text style={{ fontSize: 13 }}>{value ? dayjs(value).format('DD/MM/YYYY') : '—'}</Text>
  ),
});

const timeCol = (dataIndex) => ({
  title: <TitleWithIcon icon={<ClockCircleOutlined />}>Hora</TitleWithIcon>,
  dataIndex,
  width: 96,
  render: (value) => (
    <Text style={{ fontSize: 13 }}>{value ? dayjs(value).format('HH:mm') : '—'}</Text>
  ),
});

const idCol = () => ({
  title: <TitleWithIcon icon={<IdcardOutlined />}>N°</TitleWithIcon>,
  dataIndex: 'id',
  sorter: (a, b) => a.id - b.id,
  defaultSortOrder: 'descend',
  width: 96,
  render: (id) => <Text strong>{id}</Text>,
});

const clienteCol = (label = 'Cliente') => ({
  title: <TitleWithIcon icon={<BuildOutlined />}>{label}</TitleWithIcon>,
  dataIndex: 'Cliente',
  width: 180,
  ellipsis: true,
  render: (cliente) => (
    <Text strong style={{ textTransform: 'capitalize' }}>
      {cliente?.name || 'Sin nombre'}
    </Text>
  ),
});

const userCol = (dataIndex, label) => ({
  title: <TitleWithIcon icon={<UserOutlined />}>{label}</TitleWithIcon>,
  dataIndex,
  width: 160,
  ellipsis: true,
  render: (value) => {
    const display = typeof value === 'string' ? value : value?.fullname ?? null;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Avatar size="small" icon={<UserOutlined />} />
        <Text style={{ textTransform: 'capitalize', fontSize: 13 }}>{display || 'Sin nombre'}</Text>
      </span>
    );
  },
});

const actionsCol = (customActions, type) => ({
  title: <TitleWithIcon icon={<DownloadOutlined />}>Acciones</TitleWithIcon>,
  key: 'action',
  width: 120,
  align: 'center',
  render: customActions?.[type],
});

function TitleWithIcon({ icon, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {icon}
      <span>{children}</span>
    </span>
  );
}

function getColumns(type, customActions) {
  switch (type) {
    case 'preventivos':
      return [
        {
          title: <TitleWithIcon icon={<FileTextOutlined />}>Nombre</TitleWithIcon>,
          dataIndex: 'nombre',
          ellipsis: true,
          render: (nombre) => <Text strong>{nombre || 'Sin nombre'}</Text>,
        },
        dateCol('fecha', 'Fecha', <CalendarOutlined />),
        {
          title: <TitleWithIcon icon={<FileTextOutlined />}>Descripción</TitleWithIcon>,
          dataIndex: 'descripcion',
          ellipsis: true,
          render: (descripcion) => (
            <Tooltip title={descripcion} placement="topLeft">
              <Text>{descripcion || 'Sin descripción'}</Text>
            </Tooltip>
          ),
        },
      ];
    case 'mantencionesDep':
      return [
        idCol(),
        clienteCol('Edificio'),
        dateCol('insert_in', 'Fecha', <CalendarOutlined />),
        timeCol('insert_in'),
        userCol('recepcionist_name', 'Recepcionista'),
        actionsCol(customActions, type),
      ];
    case 'mantenciones':
      return [
        idCol(),
        userCol('Responsable', 'Responsable'),
        clienteCol(),
        dateCol('createdAt', 'Fecha', <CalendarOutlined />),
        timeCol('createdAt'),
        userCol('name_recep', 'Recepcionista'),
        actionsCol(customActions, type),
      ];
    case 'comprobantes':
      return [
        idCol(),
        clienteCol('Edificio'),
        dateCol('date', 'Fecha', <CalendarOutlined />),
        timeCol('date'),
        {
          title: <TitleWithIcon icon={<ExclamationCircleOutlined />}>Razón</TitleWithIcon>,
          dataIndex: 'reason',
          ellipsis: true,
          render: (value) => (
            <Tag color="red" style={{ textTransform: 'uppercase', fontWeight: 500 }}>
              {value || 'Sin razón'}
            </Tag>
          ),
        },
        actionsCol(customActions, type),
      ];
    case 'odt':
      return [
        {
          ...idCol(),
          dataIndex: 'numeroOrden',
          render: (numeroOrden) => <Text strong>{numeroOrden}</Text>,
        },
        {
          title: <TitleWithIcon icon={<BuildOutlined />}>Cliente</TitleWithIcon>,
          dataIndex: 'cliente',
          width: 180,
          ellipsis: true,
          render: (cliente) => (
            <Text strong style={{ textTransform: 'capitalize' }}>
              {cliente || 'Sin cliente'}
            </Text>
          ),
        },
        dateCol('fecha', 'Fecha', <CalendarOutlined />),
        {
          title: <TitleWithIcon icon={<CheckCircleOutlined />}>Estado</TitleWithIcon>,
          dataIndex: 'estado',
          render: (estado) => (
            <Tag color="blue" icon={<CheckCircleOutlined />} style={{ fontWeight: 500 }}>
              {estado || 'Sin estado'}
            </Tag>
          ),
        },
      ];
    case 'presupuestos':
      return [
        idCol(),
        clienteCol('Edificio'),
        {
          title: <TitleWithIcon icon={<FileTextOutlined />}>Razón</TitleWithIcon>,
          dataIndex: 'reason',
          ellipsis: true,
          width: 160,
          render: (reason) => (
            <Tooltip title={reason} placement="topLeft">
              <Text>{reason || 'Sin razón especificada'}</Text>
            </Tooltip>
          ),
        },
        dateCol('createdOn', 'Fecha', <CalendarOutlined />),
        timeCol('createdOn'),
        {
          title: <TitleWithIcon icon={<CheckCircleOutlined />}>Estado</TitleWithIcon>,
          dataIndex: 'status',
          width: 150,
          render: (status) => (
            <Tag
              color={coloresEstados[status]?.color ?? 'default'}
              icon={<CheckCircleOutlined />}
              style={{ fontWeight: 500 }}
            >
              {coloresEstados[status]?.description ?? 'SIN DEFINIR'}
            </Tag>
          ),
        },
        actionsCol(customActions, type),
      ];
    default:
      return [];
  }
}

export default whitDoc;
